import React, { useEffect, useState, Suspense } from "react";
import { GridView, SvgComponent } from "components";
import usePaginationValuesWhere from '../../hooks/usePaginationValuesWhere';
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import Select from "react-select";
import { Cliente } from "models/ECF/Cliente";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import { ClienteManager } from "managers";
import Exportar from "components/Exportar/Exportar";
import SuspenseECF from "components/Suspense/SuspenseECF";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { DateTimePicker } from "components/DateTimePicker/DateTimePicker";
import { DateHelper } from "utils";
import uuid from "react-uuid";
import ClienteEdit from "./ClienteEdit";


export default function ClienteList({ hideLoading, showLoading, isActive }) {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const [idSec, setIdSec] = useState(undefined);
  const [showCanvas, setShowCanvas] = useState(false);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const [dateFilterkey, setDateFilterKey] = useState(uuid());
  const [fechaCreacionFilterkey, setFechaCreacionFilterKey] = useState(uuid());
  const { handleException } = useExceptionHandler();
  const [showExportar, setShowExportar] = useState(false);
  const { cerrarAlertas } = useMessage();

  const ESTATUS_LIST = [
    { value: "", label: "Todos" },
    ...(Cliente.ESTATUS_LIST || []),
  ];

  const TIPO_DOCUMENTO_LIST = [
    { value: "", label: "Todos" },
    ...(Cliente.TIPO_DOCUMENTO_LIST || []),
  ];

  const TIPO_COMPROBANTE_LIST = [
    { value: "", label: "Todos" },
    ...(Cliente.TIPO_COMPROBANTE_LIST || []),
  ];

  useEffect(() => {
    buscar();

    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where, isActive]);

  const updateList = () => {
    buscar();
  };

  const buscar = () => {
    const whereWithPaginationValues = {
      ...where,
      ...pagination,
      FechaCreacionInicio: where.RangoFechasCreacion?.fechaInicio,
      FechaCreacionFin: where.RangoFechasCreacion?.fechaFin,
    };
    ClienteManager.listado(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        for (let cliente of res.Data) {
          cliente["FechaCreacion"] = DateHelper.formatStringDate(
            cliente["FechaCreacion"],
            true
          );
        }
        setList(res.Data);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  // Abre el canvas: sin IdSec = nuevo, con IdSec = editar
  const handleClick = (params) => {
    if (params && params.IdSec) {
      setIdSec(params.IdSec);
    } else {
      setIdSec(undefined);
    }
    setShowCanvas(true);
  };

  const openExportar = () => {
    setShowExportar(true);
  };

  return (
    <>
      <div style={{ paddingRight: "6px" }}>
        <ListHeader
          titulo="Clientes"
          crearNuevo={handleClick}
          exportar={openExportar}
        />
      </div>

      {showExportar && (
        <Suspense fallback={<SuspenseECF />}>
          <Exportar
            PROPIEDADES_EXPORTAR={Cliente.PROPIEDADES_EXPORTAR}
            showExportar={showExportar}
            setShowExportar={setShowExportar}
            showLoading={showLoading}
            hideLoading={hideLoading}
            where={where}
            serialize={true}
            exportarCallBack={ClienteManager.exportar}
          ></Exportar>
        </Suspense>
      )}

      {showCanvas && (
        <Suspense fallback={<SuspenseECF />}>
          <ClienteEdit
            showCanvas={showCanvas}
            setShowCanvas={setShowCanvas}
            IdSec={idSec}
            hideLoading={hideLoading}
            showLoading={showLoading}
            updateList={updateList}
          />
        </Suspense>
      )}

      <GridView
        pagination={pagination}
        setPagination={setPagination}
        allowPaging={pagination.EsPaginable}
        pageSize={pagination.CantidadPorPagina}
        pageIndex={pagination.IndiceDePagina}
        lastPageNumber={pagination.PaginasTotal}
        dataSource={list}
        onPageIndexChanging={onPageIndexChanging}
        where={where}
        setWhere={setWhere}
        onReset={() => {
          setDateFilterKey(uuid());
          setFechaCreacionFilterKey(uuid());
        }}
      >
        <GridView.Column
          isField
          width="100px"
          showClearFilter={true}
          bodyRenderer={(row) => {
            return (
              <div className="float-right">
                <IconButton onClick={() => handleClick({ IdSec: row.IdSec })}>
                  <ToolTip message="Editar">
                    <div>
                      <SvgComponent iconName="pencil" height="25" width="25" />
                    </div>
                  </ToolTip>
                </IconButton>
              </div>
            );
          }}
        />

        <GridView.Column
          headerText="Id"
          dataField="IdSec"
          sortExpression="IdSec"
          width="110px"
        />

        <GridView.Column
          headerText="Tipo Documento"
          dataField="TipoDocumentoDesc"
          sortExpression="TipoDocumentoDesc"
          width="150px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={TIPO_DOCUMENTO_LIST}
                value={TIPO_DOCUMENTO_LIST.filter(function (option) {
                  return (
                    option.value ===
                    (where.TipoDocumento ? where.TipoDocumento : TIPO_DOCUMENTO_LIST[0].value)
                  );
                })}
                name="TipoDocumento"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    TipoDocumento: e.value,
                  })
                }
              />
            );
          }}
        />

        <GridView.Column
          headerText="Documento"
          dataField="Documento"
          sortExpression="Documento"
          width="200px"
        />

        <GridView.Column
          headerText="Razon Social"
          dataField="RazonSocial"
          sortExpression="RazonSocial"
          width="250px"
        />

        <GridView.Column
          headerText="Tipo Comprobante"
          dataField="TipoDesc"
          sortExpression="TipoDesc"
          width="180px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={TIPO_COMPROBANTE_LIST}
                value={TIPO_COMPROBANTE_LIST.filter(function (option) {
                  return (
                    option.value ===
                    (where.Tipo ? where.Tipo : TIPO_COMPROBANTE_LIST[0].value)
                  );
                })}
                name="Tipo"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    Tipo: e.value,
                  })
                }
              />
            );
          }}
        />

        <GridView.Column
          headerText="Telefono"
          dataField="Contacto"
          sortExpression="Contacto"
          width="200px"
        />

        <GridView.Column
          headerText="Correo"
          dataField="Correo"
          sortExpression="Correo"
          width="250px"
        />

        <GridView.Column
          date
          hideFilter
          headerText="Fecha Creacion"
          dataField="FechaCreacion"
          sortExpression="FechaCreacion"
          width="250px"
          filterRenderer={(onInputChange) => {
            return (
              <DateTimePicker
                key={fechaCreacionFilterkey}
                soloFecha
                esRangoFechas
                esGrid
                usarLabel={false}
                fechaInicioOriginal={where?.RangoFechasCreacion?.fechaInicio || ""}
                fechaFinOriginal={where?.RangoFechasCreacion?.fechaFin || ""}
                onChange={(e) =>
                  onInputChange({
                    target: { name: "RangoFechasCreacion", value: e },
                  })
                }
              />
            );
          }}
        />

        <GridView.Column
          headerText="Estatus"
          dataField="EstatusDesc"
          sortExpression="EstatusDesc"
          width="175px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={ESTATUS_LIST}
                value={ESTATUS_LIST.filter(function (option) {
                  return (
                    option.value ===
                    (where.Estatus ? where.Estatus : ESTATUS_LIST[0].value)
                  );
                })}
                name="Estatus"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    Estatus: e.value,
                  })
                }
              />
            );
          }}
        />
      </GridView>
    </>
  );
}